import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Heading, Paragraph, PlainCard, ProgressPie, Skeleton } from '@treams/treams-ui';

export default Skeleton(
	function TalentReviewCard(props: { talentReview: graphql.TalentReview; userID: string }) {
		const { t } = useTranslation();

		const hideOwnerInTalentReview = useFeature(graphql.FeatureName.HideOwnerInTalentReview);

		const numberOfPlacements = props.talentReview.placements.length;

		const totalDisplayCount = useMemo(() => {
			const totalCount = props.talentReview.group.members.totalCount;

			if (!hideOwnerInTalentReview) {
				return totalCount;
			}

			const numberOfOwners = props.talentReview.group.members.edges.filter(
				({ role }) => role.type === graphql.RoleType.Owner,
			).length;

			return totalCount - numberOfOwners;
		}, [props.talentReview, hideOwnerInTalentReview]);

		const progress = !Number.isNaN(numberOfPlacements / totalDisplayCount)
			? numberOfPlacements / totalDisplayCount
			: 0;

		return (
			<PlainCard to={`/talent-reviews/${props.talentReview.id}`}>
				<Flex alignItems="center">
					<Box py={3} flexGrow={1} flexShrink={1}>
						<Paragraph>
							{t('talent-reviews.index.card.createdAt', {
								date: moment(props.talentReview.createdAt).format('LL'),
							})}
							{' · '}
							{t('talent-reviews.index.card.progress', {
								placed: numberOfPlacements,
								total: totalDisplayCount,
							})}
						</Paragraph>
						<Heading mt={2}>{props.talentReview.title}</Heading>
					</Box>

					<Box>
						<ProgressPie progress={progress} />
					</Box>
				</Flex>
			</PlainCard>
		);
	},
	function TalentReviewSkeleton() {
		return (
			<PlainCard.Skeleton>
				<Flex alignItems="center">
					<Box py={3} flexGrow={1} flexShrink={1}>
						<Paragraph.Skeleton lines={1} />
						<Heading.Skeleton mt={2} />
					</Box>
					<Box>
						<ProgressPie.Skeleton />
					</Box>
				</Flex>
			</PlainCard.Skeleton>
		);
	},
);
